import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import searchIcon from "./../../media/search-icon.svg";
import Logger from "../../utils/Logger";
import { LoadingComponent } from "../../App.js";
import httpClient from "../../services/httpClient";

const UPS = () => {
  const { t } = useTranslation();
  const [trackingId, setTrackingId] = useState("");
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!trackingId.trim()) {
      setError(t("logistics.enterTrackingNumber"));
      setTrackingData(null);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      Logger.log(`Śledzenie przesyłki UPS: ${trackingId}`);
      const response = await httpClient.get(`/Ups/tracking/${trackingId}`, {
        withAuth: true,
      });

      setTrackingData(response.data);
      Logger.log("Dane śledzenia UPS otrzymane:", response.data);
    } catch (error) {
      Logger.error("Błąd podczas śledzenia przesyłki UPS:", error);
      if (error.response?.status === 404) {
        setError(t("logistics.noParcelFound"));
      } else if (error.response?.status === 400) {
        setError(t("logistics.invalidTrackingNumber"));
      } else {
        setError(t("logistics.errorTracking"));
      }
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    const s = status.toLowerCase();
    if (s.includes("doręczono")) return "green";
    if (s.includes("doręczeniu")) return "#8B4513"; // brązowy
    if (s.includes("dotarło")) return "blue";
    if (s.includes("wyjechało")) return "purple";
    if (s.includes("skan")) return "teal";
    return "black";
  };

  const getShipmentInfo = () => {
    if (!trackingData || !trackingData.length) return null;
    const shipment = trackingData[0];
    const pkg = shipment.packages?.[0];
    if (!pkg) return null;
    return { shipment, package: pkg };
  };

  const shipmentInfo = getShipmentInfo();

  return (
    <div className="ups-service-checker">
      <h1>{t("logistics.upsTracking")}</h1>

      <form className="formularz" onSubmit={handleSubmit}>
        <label htmlFor="trackingId">
          <div className="input-container">
            <input
              type="text"
              id="trackingId"
              placeholder={t("logistics.trackingNumber")}
              value={trackingId}
              onChange={(e) => setTrackingId(e.target.value)}
            />
            <img
              src={searchIcon}
              alt="search"
              className="search-icon"
              onClick={handleSubmit}
            />
          </div>
        </label>
        <div className="check">
          <input
            type="submit"
            value={loading ? t("logistics.checking") : t("logistics.track")}
            disabled={loading}
          />
        </div>
      </form>

      {error && <div className="error">{error}</div>}
      {loading && hasSearched && <LoadingComponent />}

      {shipmentInfo && !loading && (
        <div className="tracking-info">
          <h2>
            Informacje o przesyłce <b>{shipmentInfo.shipment.shipmentNumber}</b>
          </h2>

          {shipmentInfo.package.deliveryInfo?.receivedBy && (
            <p>
              Odebrana przez: {shipmentInfo.package.deliveryInfo.receivedBy}
            </p>
          )}

          <div className="package-info">
            <p>
              <strong>Status:</strong> {shipmentInfo.package.currentStatus}
            </p>
            <p>
              <strong>Waga:</strong> {shipmentInfo.package.weight}
            </p>
            <p>
              <strong>Usługa:</strong> {shipmentInfo.package.service}
            </p>
            <p>
              <strong>Data pobrania:</strong> {shipmentInfo.shipment.pickupDate}
            </p>
            <p>
              <strong>Typ przesyłki:</strong>{" "}
              {shipmentInfo.shipment.shipmentType}
            </p>
          </div>

          <h3>Historia przesyłki:</h3>
          <div className="tracking-timeline">
            {shipmentInfo.package.updates?.length > 0 ? (
              shipmentInfo.package.updates.map((event, index) => (
                <div key={index} className="tracking-event">
                  <div className="event-content">
                    <div className="event-header">
                      <span
                        className="event-status"
                        style={{ color: getStatusColor(event.status) }}
                      >
                        {event.status}
                      </span>

                      {(event.location?.city || event.location?.country) && (
                        <span className="event-location">
                          {event.location?.city}
                          {event.location?.city && event.location?.country
                            ? ", "
                            : ""}
                          {event.location?.country}
                        </span>
                      )}

                      <span className="event-timestamp">
                        {formatTimestamp(event.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p>Brak danych o wydarzeniach dla tej przesyłki.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UPS;
